import { captureMessage } from "@sentry/remix";
import keyBy from "lodash/keyBy";
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { WindowWithEasySize, WindowWithENV } from "~/entities/global/types";
import { Product, ProductAvailability } from "~/entities/product/types";
import { User } from "~/entities/user/types";
import { useHydrated } from "~/hooks/useHydrated";
import "./easySize.css";

const buildEasySizeConfigFrom = (
  product: Product,
  productAvailability: ProductAvailability,
  formRef: React.RefObject<HTMLFormElement>,
) => ({
  product_id: product.id,
  product_type: product.category?.name.toLowerCase() ?? "",
  product_brand: "ShirtSpace",
  product_gender: product.cuts?.join(", ") || "unisex",
  user_id: JSON.parse(sessionStorage.getItem("user") ?? "{}")?.["id"] ?? "-1",
  loaded: () => {
    const formElement = formRef.current;
    if (!formElement) return false;

    const inputs = formElement.querySelectorAll('input[type="number"]');
    inputs.forEach((input) => {
      (input as HTMLInputElement).value = "";
    });
  },
  shop_id: (window as WindowWithENV).ENV?.EASY_SIZE_APP_ID,
  add_to_cart_btn_selector: ".add-to-cart__button",
  placeholder: ".add-to-cart__easy-size",
  cookies_enabled: "true",
  size_update_function: (size: string) => {
    const formElement = formRef.current;

    if (!formElement) return false;

    const inputs = formElement.querySelectorAll('input[type="number"]');

    inputs.forEach((input) => {
      (input as HTMLInputElement).value = "";
    });

    // Set the selected size quantity to 1
    const sizeElement = formElement.querySelector(
      `#size-input__${size.toUpperCase()}`,
    ) as HTMLInputElement;

    if (sizeElement) {
      sizeElement.value = "1";
      return true;
    }

    return false;
  },
  sizes_in_stock: (() => {
    const variants = keyBy(product.variants, "id");

    return productAvailability.inventories.reduce(
      (acc, { id, inventory }) => {
        const variant = variants[id];

        if (!variant) return acc;

        return {
          ...acc,
          [variant.size]: inventory,
        };
      },
      {} as Record<string, number>,
    );
  })(),
});

type Params = {
  product: Product;
  productAvailability: Promise<ProductAvailability | null>;
  formRef: React.RefObject<HTMLFormElement>;
  user: User | null;
};

export function useEasySize({
  product,
  productAvailability,
  formRef,
  user,
}: Params) {
  if (user?.doNotSellMyInformation)
    return {
      pageViewID: undefined,
    };

  const isHydrated = useHydrated();
  const [resolvedProductAvailability, setResolvedProductAvailability] =
    useState<ProductAvailability | null | undefined>(undefined);
  const [pageViewID, setPageViewID] = useState<string | undefined>(undefined);
  const outletContext = useOutletContext();

  useEffect(() => {
    if (!productAvailability || resolvedProductAvailability !== undefined)
      return;
    productAvailability.then((resolvedProductAvailability) => {
      setResolvedProductAvailability(resolvedProductAvailability);
    });
  }, [productAvailability]);

  useEffect(() => {
    if (!isHydrated || !product || !resolvedProductAvailability || !formRef)
      return;

    const easySizeConfig = buildEasySizeConfigFrom(
      product,
      resolvedProductAvailability,
      formRef,
    );

    const windowWithEasySize = window as WindowWithEasySize;
    const EasySize = windowWithEasySize?.EasySize;
    const EasySizeParametersDebug = windowWithEasySize?.EasySizeParametersDebug;

    if (!EasySize || !EasySizeParametersDebug) return;

    try {
      const easysize = new EasySize(easySizeConfig);

      easysize.start();

      setPageViewID(EasySizeParametersDebug.easysize.pageview_id);
    } catch (error) {
      captureMessage("EasySize failed to start", {
        extra: { error, outletContext },
      });
    }
  }, [isHydrated, product, resolvedProductAvailability]);

  return { pageViewID };
}
